// Pure helpers for the "Written Sales" dashboard widget. Kept free of
// React/redux so the aggregation can be unit tested in isolation (mirrors
// revenueProfitHelpers / newCustomersSecuredHelpers).
//
// Business logic: written Sales (excl. tax) from combined sales documents
// (open/released orders etc.), bucketed into Month-To-Date and Year-To-Date,
// overall and per salesperson. This is the Revenue & Profit widget's revenue
// half — there is no profit here.
//
// Live data comes from the CombinedSalesDocs endpoint: the widget fetches it and
// passes each document row through `mapCombinedSalesDocRows` (below) into the
// record shape this aggregation consumes, so the summarization stays agnostic of
// the data source and remains unit-testable on its own.

import { getPeriodRanges, toDay } from "./newCustomersSecuredHelpers";

// Reuse the shared period windows (MTD / YTD) so the date logic lives in one place.
export { getPeriodRanges } from "./newCustomersSecuredHelpers";

const inRange = (day, range) => day >= range.fromDate && day <= range.toDate;

// Format a number as whole-dollar USD (e.g. 3500 -> "$3,500").
export const formatCurrency = (value) => {
  const n = Number(value) || 0;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

// Summarize written-sales from raw records. Each record:
//   { salesPerson, writtenDate, sales }
// Returns:
//   totals: { salesMtd, salesYtd }
//   reps:   [{ repName, salesMtd, salesYtd }] (YTD desc)
// Records outside the year-to-date window (or without a usable date) are ignored.
export const summarizeWrittenSales = (records, now = new Date()) => {
  const ranges = getPeriodRanges(now);
  const totals = { salesMtd: 0, salesYtd: 0 };
  const repMap = new Map();

  (Array.isArray(records) ? records : []).forEach((r) => {
    if (!r) return;
    const day = toDay(r.writtenDate);
    if (!day || !inRange(day, ranges.year)) return; // YTD bounds everything
    const sales = Number(r.sales) || 0;
    const isMonth = inRange(day, ranges.month);
    const repName = (r.salesPerson || "").trim() || "Unassigned";

    const rep = repMap.get(repName) || { repName, salesMtd: 0, salesYtd: 0 };

    totals.salesYtd += sales;
    rep.salesYtd += sales;
    if (isMonth) {
      totals.salesMtd += sales;
      rep.salesMtd += sales;
    }
    repMap.set(repName, rep);
  });

  const reps = Array.from(repMap.values()).sort(
    (a, b) => b.salesYtd - a.salesYtd || a.repName.localeCompare(b.repName)
  );

  return { totals, reps };
};

// ---------------------------------------------------------------------------
// Live data mapper. The "Written Sales" widget fetches the CombinedSalesDocs
// endpoint and runs its `result` rows through this to get the record shape
// summarizeWrittenSales consumes. Kept pure (no axios/config) so it stays unit-
// testable and the network concern lives in the widget.
// ---------------------------------------------------------------------------

// Map raw CombinedSalesDocs rows to written-sales records. Each API row:
//   { entryNo, documentSource, bcRecordNo, nancyJobNo, salespersonName,
//     documentDate, sales, location, division }
// becomes { salesPerson, writtenDate, sales }. A blank salespersonName is
// preserved as "" (summarizeWrittenSales buckets those under "Unassigned");
// non-array input yields [] and falsy rows are skipped.
export const mapCombinedSalesDocRows = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce((acc, r) => {
    if (!r) return acc;
    acc.push({
      salesPerson: (r.salespersonName || "").trim(),
      writtenDate: r.documentDate,
      sales: Number(r.sales) || 0,
    });
    return acc;
  }, []);
